import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useExploreStore, useChatInputStore, useToastStore, useAuthStore, useModalStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import { lightTap } from '@/constants/utils/haptics';
import copyText from '@/constants/utils/copy_text';

const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

const ExploreCardDropdown = React.memo(({ item, children }) => {
  const router = useRouter();
  const cloneItem = useExploreStore(state => state.actions.cloneItem);
  const addToast = useToastStore(state => state.actions.addToast);
  const openModal = useModalStore(state => state.actions.openModal);
  const token = useAuthStore(state => state.token);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);

  const handleUsePrompt = useCallback(async () => {
    lightTap();
    if (!token || isGhost) {
      openModal(isGhost ? 'GhostSignupModal' : 'SignupModal');
      return;
    }
    trackEvent('explore', 'explore_clone', item.slug);
    try {
      const clone = await cloneItem(item.slug);
      if (!clone) return;
      const store = useChatInputStore.getState();
      const prompt = clone.prompt || item.prompt;
      // Use the resolved Model document's _id — `clone.modelSlug` is a
      // human-readable slug (e.g. "imagen-4-0") which the chat backend
      // can't cast to ObjectId.
      const modelId = clone.model?.id || null;
      if (item.mediaType === 'video') {
        store.initializeVideoGeneration(prompt, modelId, true);
      } else if (item.mediaType === 'music') {
        store.initializeMusicGeneration(prompt, modelId, true, clone.musicContext?.description || null);
      } else {
        store.initializeImageGeneration(prompt, modelId, true);
      }
      router.push('/app');
    } catch {
      addToast('Clone failed', 'error');
    }
  }, [item, cloneItem, router, token, isGhost, openModal, addToast]);

  const handleCopy = useCallback(async () => {
    lightTap();
    if (!item?.prompt) return;
    trackEvent('explore', 'explore_copy_prompt', item.slug);
    try {
      await copyText(item.prompt);
      addToast('Prompt copied ✓', 'success');
    } catch {
      addToast('Failed to copy', 'error');
    }
  }, [item, addToast]);

  const handleShare = useCallback(async () => {
    lightTap();
    trackEvent('explore', 'explore_share', item?.slug);
    try {
      const { Share } = require('react-native');
      const shareUrl = `https://gab.ai/explore/item/${item?.slug}`;
      await Share.share({
        message: `Check out this AI creation on Gab AI: ${shareUrl}`,
        url: shareUrl,
      });
    } catch {
      // User cancelled
    }
  }, [item]);

  if (!isNative) {
    return children;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger action="longPress">
        {children}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Item key="use-prompt" onSelect={handleUsePrompt}>
          <DropdownMenu.ItemIcon ios={{ name: 'sparkles' }} />
          <DropdownMenu.ItemTitle>Use This Prompt</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
        <DropdownMenu.Item key="copy" onSelect={handleCopy}>
          <DropdownMenu.ItemIcon ios={{ name: 'doc.on.doc' }} />
          <DropdownMenu.ItemTitle>Copy Prompt</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
        <DropdownMenu.Item key="share" onSelect={handleShare}>
          <DropdownMenu.ItemIcon ios={{ name: 'square.and.arrow.up' }} />
          <DropdownMenu.ItemTitle>Share</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

ExploreCardDropdown.displayName = 'ExploreCardDropdown';

export default ExploreCardDropdown;
